import { useEffect, useState } from 'react';

/**
 * Seconds left until a server-set deadline, ticking once a second.
 *
 * `phaseEndsAt` is the deadline the server stores for the negotiate/decide clock;
 * `serverNow` is the server's own clock at the moment it sent that deadline. Phones and
 * laptops in one room routinely disagree about the time by seconds or minutes, so the
 * difference between the two clocks is measured once per payload and every tick is
 * corrected by it — otherwise each screen would show a different time left.
 *
 * Returns null when no clock is running, 0 once time is up (never negative).
 */
const useCountdown = (phaseEndsAt, serverNow) => {
  const [secondsLeft, setSecondsLeft] = useState(null);

  useEffect(() => {
    if (!phaseEndsAt) {
      setSecondsLeft(null);
      return undefined;
    }
    const end = new Date(phaseEndsAt).getTime();
    if (!Number.isFinite(end)) {
      setSecondsLeft(null);
      return undefined;
    }
    // Positive when this device's clock is behind the server's.
    const skew = typeof serverNow === 'number' ? serverNow - Date.now() : 0;
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((end - (Date.now() + skew)) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [phaseEndsAt, serverNow]);

  return secondsLeft;
};

/** 125 -> "2:05". */
export const formatClock = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export default useCountdown;
